import {
  CopyPlus,
  EllipsisVertical,
  LucideIcon,
  Pen,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useGetProject } from "~/api/projects/projects";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useProjectParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { CreateVersionModal } from "../CreateVersionDialog";
import { DeleteVersionDialog } from "../DeleteVersionDialog";
import { RenameVersionDialog } from "../RenameVersionDialog";

interface VolumeActionsProps {
  className?: string;
  isMaster?: boolean;
  disableClone?: boolean;
}

interface DropdownItem {
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  onClick: () => void;
  disabled?: boolean;
}

export const VolumeActions: FC<VolumeActionsProps> = ({
  className,
  isMaster,
  disableClone,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { data: currentVersionCase } = useGetProject(organizationId, caseId);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({ eventName: "app/volume_actions.dropdown/open" });
    }
  }, [dropdownOpen]);

  const dropdownItems: (DropdownItem | false)[] = [
    {
      icon: CopyPlus,
      messageId: "clone",
      defaultMessage: "Clone",
      onClick: () => {
        trackEvent({ eventName: "app/volume_actions_menu/clone.link/click" });
        setShowCloneDialog(true);
        setDropdownOpen(false);
      },
      disabled: disableClone,
    },
    !isMaster && {
      icon: Pen,
      messageId: "rename",
      defaultMessage: "Rename",
      onClick: () => {
        trackEvent({ eventName: "app/volume_actions_menu/rename.link/click" });
        setShowRenameDialog(true);
        setDropdownOpen(false);
      },
    },
    !isMaster && {
      icon: Trash2,
      messageId: "delete",
      defaultMessage: "Delete",
      onClick: () => {
        trackEvent({ eventName: "app/volume_actions_menu/delete.link/click" });
        setShowDeleteDialog(true);
        setDropdownOpen(false);
      },
    },
  ];

  const filteredDropdownItems = dropdownItems.filter(
    (item): item is DropdownItem => item !== false
  );

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={className}>
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[160px] w-auto" align="start">
          {filteredDropdownItems.map((item) => (
            <DropdownMenuItem
              key={item.messageId}
              onClick={item.disabled ? undefined : item.onClick}
              destructive={item.messageId === "delete"}
              disabled={item.disabled}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <FormattedMessage
                id={item.messageId}
                defaultMessage={item.defaultMessage}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {showCloneDialog && (
        <CreateVersionModal
          open={showCloneDialog}
          setOpen={setShowCloneDialog}
          documentsToInclude={[]}
          defaultName={`${currentVersionCase?.name} (copy)`}
          customDescription="When creating a version, all documents, settings will be duplicated in the new version. Doc list is preserved."
        />
      )}

      {showRenameDialog && (
        <RenameVersionDialog
          open={showRenameDialog}
          setOpen={setShowRenameDialog}
        />
      )}

      {showDeleteDialog && (
        <DeleteVersionDialog
          open={showDeleteDialog}
          setOpen={setShowDeleteDialog}
          projectId={caseId}
        />
      )}
    </>
  );
};
